import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../AuthContext';
import { captureAttribution, getAttribution, isInAppBrowser } from '../utils/attribution';

const TRUST = [
  'Scan and list across every channel',
  'AI pricing from real comp data',
  'Real profit after fees, per card',
];

// Only allow internal relative paths (prevents open-redirect via ?next=//evil.com).
// Used by the team-invite flow: a new teammate lands on /accept-invite, not onboarding.
function safeNext(fallback) {
  try {
    const n = new URLSearchParams(window.location.search).get('next');
    if (n && n.startsWith('/') && !n.startsWith('//')) return n;
  } catch { /* ignore */ }
  return fallback;
}

function Register() {
  const navigate = useNavigate();
  const { register, googleLogin } = useAuth();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  // Referral code, pre-filled if they arrived via a ?ref= link/QR, but also
  // typeable so someone reading a code off a printed flyer can enter it.
  const [refCode, setRefCode] = useState('');
  // Google OAuth is blocked inside social-app in-app browsers (Reddit, IG,
  // etc.). Showing a dead button there kills signups, hide it and point the
  // user at the email form instead.
  const inApp = isInAppBrowser();

  // Also run capture in case user landed directly on /register
  useEffect(() => {
    captureAttribution();
    const a = getAttribution();
    if (a.ref) setRefCode(a.ref);
  }, [searchParams]);

  const getUtmData = () => {
    const { signupSource, signupCampaign, signupMedium, gclid, rdt_cid, fbclid, fbp, fbc, signupLandingPage, signupReferrer } = getAttribution();
    // Trybe attribution visitor ID, set by the pixel when a creator's link sent
    // this visitor. Captured here so a later subscription can be reported to
    // Trybe's orders API and credit the creator. Guarded, pixel may be blocked.
    let trybeVid = null;
    try { trybeVid = window.trybe?.getVisitorId?.() || null; } catch (e) { /* pixel not loaded */ }
    // The form field is the source of truth for ref, it's pre-filled from
    // the captured ?ref= but a user can also type a code off a flyer.
    // ?variant=trial|free, a test override that forces the A/B bucket so the
    // founder can exercise the trial wall without flipping the global flag.
    const variant = searchParams.get('variant');
    return { signupSource, signupCampaign, signupMedium, gclid, rdt_cid, fbclid, fbp, fbc, signupLandingPage, signupReferrer, trybeVid, ref: (refCode || '').trim() || null, ...(variant ? { variant } : {}) };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await register(email, password, name, getUtmData());
      // Fire Google Ads sign-up conversion
      if (window.gtag) {
        window.gtag('event', 'conversion', {
          send_to: 'AW-18013782383/gpfPCNGC1YwcEO-C0o1D',
          value: 1.0,
          currency: 'USD',
        });
      }
      // Fire Reddit sign-up conversion
      if (window.rdt) {
        window.rdt('track', 'SignUp');
      }
      try {
        localStorage.removeItem('scn-onboarded');
        localStorage.removeItem('scn-tour-completed');
        localStorage.removeItem('scn-tour-step');
        localStorage.removeItem('scn-onboarding-step'); // fresh signup starts at welcome
      } catch {}
      // Hard navigation (not SPA navigate) so a stale lazy-chunk after a deploy
      // can't blank the screen at the most critical moment, right after signup.
      window.location.href = safeNext('/onboarding');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    if (loading) return;
    setLoading(true);
    try {
      setError('');
      await googleLogin(credentialResponse.credential, getUtmData());
      // Fire Google Ads sign-up conversion
      if (window.gtag) {
        window.gtag('event', 'conversion', {
          send_to: 'AW-18013782383/gpfPCNGC1YwcEO-C0o1D',
          value: 1.0,
          currency: 'USD',
        });
      }
      // Fire Reddit sign-up conversion
      if (window.rdt) {
        window.rdt('track', 'SignUp');
      }
      try {
        localStorage.removeItem('scn-onboarded');
        localStorage.removeItem('scn-tour-completed');
        localStorage.removeItem('scn-tour-step');
        localStorage.removeItem('scn-onboarding-step'); // fresh signup starts at welcome
      } catch {}
      window.location.href = safeNext('/onboarding');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to sign up with Google');
    } finally {
      setLoading(false);
    }
  };

  const inp = (name) => ({
    width: '100%', padding: '12px 15px', borderRadius: 11,
    border: `1px solid ${focused === name ? '#1c56cc' : 'rgba(255,255,255,0.11)'}`,
    background: focused === name ? 'rgba(28, 86, 204,0.07)' : 'rgba(255,255,255,0.035)',
    color: 'white', fontSize: 14.5, boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none',
    transition: 'border-color 0.15s ease, background 0.15s ease',
  });

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', -apple-system, sans-serif", background: 'radial-gradient(1100px 560px at 50% -12%, #101a35 0%, #070a12 58%)', padding: 'clamp(20px, 5vw, 44px)' }}>
      <div style={{ width: '100%', maxWidth: 424 }}>

        {/* Wordmark, links back to the homepage so a signup can go research more */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 11, marginBottom: 22, textDecoration: 'none' }}>
          <div style={{ width: 34, height: 34, background: '#1c56cc', borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: 'white', fontFamily: 'monospace' }}>SC</div>
          <span style={{ fontSize: 18, fontWeight: 700, color: 'white', letterSpacing: '-0.3px' }}>SportsCard<span style={{ color: '#1c56cc' }}>Network</span></span>
        </Link>
        <div style={{ textAlign: 'center', marginTop: -12, marginBottom: 18 }}>
          <Link to="/" style={{ color: '#7b899e', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>← Back to homepage</Link>
        </div>

        {/* Card */}
        <div style={{ background: 'rgba(255,255,255,0.028)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 18, padding: 'clamp(24px, 5vw, 34px)', boxShadow: '0 24px 60px rgba(0,0,0,0.45)' }}>

          <h1 style={{ fontSize: 24, fontWeight: 700, color: 'white', margin: '0 0 6px', letterSpacing: '-0.4px', textAlign: 'center' }}>Create your account</h1>
          <p style={{ fontSize: 13.5, color: '#7b899e', margin: '0 0 24px', textAlign: 'center' }}>14-day free trial · Cancel anytime</p>

          {error && <div style={{ background: 'rgba(220,38,38,0.1)', color: '#f87171', padding: '10px 14px', borderRadius: 10, marginBottom: 16, fontSize: 13, border: '1px solid rgba(220,38,38,0.2)' }}>{error}</div>}

          {inApp ? (
            <div style={{ background: 'rgba(28, 86, 204,0.08)', border: '1px solid rgba(28, 86, 204,0.2)', color: '#94a3b8', padding: '11px 14px', borderRadius: 10, marginBottom: 20, fontSize: 12.5, lineHeight: 1.55 }}>
              Create your account with email below. Google sign-up isn't available inside an in-app browser, to use it, open this page in Safari or Chrome (tap the <strong style={{ color: '#cbd5e1' }}>⋯</strong> menu, then "Open in browser").
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 18, display: 'flex', justifyContent: 'center' }}>
                <GoogleLogin onSuccess={handleGoogleSuccess} onError={() => setError('Google sign-up failed')} size="large" width="360" text="signup_with" theme="filled_black" />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, color: '#4b5568', fontSize: 12 }}>
                <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
                <span>or</span>
                <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
              </div>
            </>
          )}

          <form onSubmit={handleSubmit}>
            {[
              { label: 'Full name', type: 'text', val: name, set: setName, key: 'name', ph: 'Your name', autoComplete: 'name', autoCapitalize: 'words' },
              { label: 'Email address', type: 'email', val: email, set: setEmail, key: 'email', ph: 'you@example.com', req: true, autoComplete: 'email', inputMode: 'email', autoCapitalize: 'off', autoCorrect: 'off', spellCheck: false },
              { label: 'Password', type: 'password', val: password, set: setPassword, key: 'password', ph: 'At least 6 characters', req: true, autoComplete: 'new-password' },
              { label: 'Referral code (optional)', type: 'text', val: refCode, set: setRefCode, key: 'ref', ph: 'Have a code from a flyer? Enter it', autoCapitalize: 'characters', autoCorrect: 'off', spellCheck: false },
            ].map(({ label, type, val, set, key, ph, req, autoComplete, inputMode, autoCapitalize, autoCorrect, spellCheck }) => (
              <div key={key} style={{ marginBottom: 15 }}>
                <label htmlFor={`reg-${key}`} style={{ display: 'block', fontSize: 12.5, fontWeight: 500, color: '#8a97ab', marginBottom: 7 }}>{label}</label>
                <div style={{ position: 'relative' }}>
                  <input id={`reg-${key}`} name={key} type={key === 'password' && showPassword ? 'text' : type} autoComplete={autoComplete} inputMode={inputMode} autoCapitalize={autoCapitalize} autoCorrect={autoCorrect} spellCheck={spellCheck} value={val} onChange={e => set(e.target.value)} onFocus={() => setFocused(key)} onBlur={() => setFocused(null)} placeholder={ph} required={req} style={key === 'password' ? { ...inp(key), paddingRight: 60 } : inp(key)} />
                  {key === 'password' && (
                    <button type="button" onClick={() => setShowPassword(s => !s)} tabIndex={-1}
                      style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 12, fontWeight: 600, padding: '12px 8px' }}>
                      {showPassword ? 'Hide' : 'Show'}
                    </button>
                  )}
                </div>
              </div>
            ))}
            <button type="submit" disabled={loading} style={{ width: '100%', padding: 14, background: '#1c56cc', color: 'white', border: 'none', borderRadius: 11, fontSize: 15.5, fontWeight: 700, cursor: loading ? 'wait' : 'pointer', fontFamily: 'inherit', opacity: loading ? 0.7 : 1, marginTop: 6, boxShadow: '0 8px 22px rgba(28, 86, 204,0.35)' }}>
              {loading ? 'Creating account…' : 'Start free →'}
            </button>
          </form>

          <div style={{ textAlign: 'center', fontSize: 13, color: '#7b899e', marginTop: 18 }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: '#6b8aff', textDecoration: 'none', fontWeight: 600 }}>Sign in</Link>
          </div>
        </div>

        {/* Trust strip */}
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '6px 16px', marginTop: 20 }}>
          {TRUST.map((t, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, color: '#6b7688' }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#1c56cc', flexShrink: 0 }} />{t}
            </span>
          ))}
        </div>

      </div>
    </div>
  );
}

export default Register;
